<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { countFillableFields, pageOrientation } from '@record-fill';
  import { Loader2 } from 'lucide-vue-next';
  import { useCustomToast, setBreadcrumbLabel, clearBreadcrumbLabel } from '@/shared/lib';
  import {
    matrixBuilderApi,
    FormBuilderViewerStage,
    FormBuilderFooter,
    useFormBuilderStore,
    type Matrix,
    type FormStructure,
  } from '@/features/FormBuilder';

  const route = useRoute();
  const router = useRouter();
  const { showErrorToast } = useCustomToast();
  const formBuilderStore = useFormBuilderStore();

  const draft = ref<Matrix | null>(null);
  const isLoading = ref(true);
  const hasError = ref(false);

  const zoom = ref(1);
  const pageCount = ref(1);
  const currentPage = ref(1);
  const viewScrollTop = ref(0);

  const scrollRef = ref<HTMLElement | null>(null);
  const canvasWrapperRef = ref<HTMLElement | null>(null);
  const canvasOffsetTop = ref<number | null>(null);

  const draftId = computed(() => Number(route.params.id));

  const pageTitle = computed(() =>
    draft.value ? `${draft.value.prefix} - ${draft.value.title}` : ''
  );

  const totalFields = computed(() => countFillableFields(formBuilderStore.elements));

  const viewerStructure = computed<FormStructure | null>(() => {
    if (!draft.value) return null;
    return {
      elements: draft.value.matrixStructure.elements,
      version: draft.value.matrixStructure.version,
      metadata: draft.value.matrixStructure.metadata,
    };
  });
  const orientation = computed(() => pageOrientation(viewerStructure.value));

  function handleScroll(): void {
    const scrollEl = scrollRef.value;
    const canvasEl = canvasWrapperRef.value;
    if (!scrollEl || !canvasEl) return;
    if (canvasOffsetTop.value === null) {
      canvasOffsetTop.value =
        canvasEl.getBoundingClientRect().top -
        scrollEl.getBoundingClientRect().top +
        scrollEl.scrollTop;
    }
    viewScrollTop.value = Math.max(0, scrollEl.scrollTop - canvasOffsetTop.value);
  }

  async function fetchDraft(): Promise<void> {
    isLoading.value = true;
    hasError.value = false;
    try {
      const result = await matrixBuilderApi.loadMatrix(draftId.value);
      if (!result.isDraft) {
        throw new Error('Matrix is no longer a draft');
      }
      draft.value = result;
      setBreadcrumbLabel(result.title);
    } catch (error) {
      console.error('Error fetching draft matrix:', error);
      hasError.value = true;
      showErrorToast({
        title: 'Erro ao carregar rascunho',
        type: 'error',
        hideIcon: true,
      });
      router.push({ name: 'GestaoRegistrosModelos', query: { tab: 'rascunhos' } });
    } finally {
      isLoading.value = false;
    }
  }

  onMounted(fetchDraft);
  watch(draftId, fetchDraft);

  onBeforeUnmount(() => {
    clearBreadcrumbLabel();
  });
</script>

<template>
  <div class="flex h-full flex-col">
    <div
      v-if="isLoading"
      class="flex flex-1 items-center justify-center"
      data-testid="view-draft-loading"
    >
      <Loader2 class="h-6 w-6 animate-spin text-[var(--theme-secondary-ink)]" />
    </div>

    <template v-else-if="draft && viewerStructure">
      <div
        ref="scrollRef"
        class="min-h-0 flex-1 overflow-auto [scrollbar-gutter:stable] px-8 pt-6 pb-6"
        data-testid="view-draft-scroll"
        @scroll="handleScroll"
      >
        <h1 class="text-2xl font-bold text-[var(--color-gray-07)]">
          {{ pageTitle }}
        </h1>

        <div
          ref="canvasWrapperRef"
          class="mt-6"
        >
          <FormBuilderViewerStage
            :structure="viewerStructure"
            :zoom="zoom"
            :view-scroll-top="viewScrollTop"
            force-readonly
            restore-draft-on-unmount
            @update:zoom="zoom = $event"
            @update:page-count="pageCount = $event"
            @update:current-page="currentPage = $event"
          />
        </div>
      </div>

      <FormBuilderFooter
        class="shrink-0"
        mode="view"
        :orientation="orientation"
        :zoom="zoom"
        :page-count="pageCount"
        :current-page="currentPage"
        :total-fields="totalFields"
        @update:zoom="zoom = $event"
      />
    </template>
  </div>
</template>
